import { logBreadcrumb, logErrorWithContext } from 'shared/utils/sentry/sentry';
import {
  ErrorSeverity,
  FeatureArea,
  ResponsibleTeam,
} from 'shared/utils/sentry/sentry.constants';
import { CombinedError } from 'urql';

import {
  ClassifiedErrorType,
  classifyError,
  isAbortLikeError,
} from './classifyError';
import {
  AuthenticationError,
  BaseNetworkError,
  ClientError,
  GraphQLOperationError,
  NetworkError,
  RequestConfigError,
  ServerError,
} from './NetworkErrorClasses';

/**
 * Selective logging policy — only actionable errors create Sentry events.
 * Everything else leaves a breadcrumb so the debugging trail is preserved
 * without creating alert noise.
 *
 * | type    | Sentry event | Rationale                                     |
 * | ------- | ------------ | --------------------------------------------- |
 * | server  | yes          | 5xx — backend issues we can investigate       |
 * | graphql | yes          | schema/resolver issues                        |
 * | unknown | yes          | unclassified — investigate                    |
 * | client  | no           | expected validation failures                  |
 * | auth    | no           | expected token expiration                     |
 * | network | no           | transient connectivity on the user's device   |
 * | timeout | no           | transient or intentional cancellation         |
 * | abort   | no           | intentional cancellation                      |
 */
const SENTRY_EVENT_POLICY: Record<ClassifiedErrorType, ErrorSeverity | null> = {
  server: ErrorSeverity.HIGH,
  graphql: ErrorSeverity.MEDIUM,
  unknown: ErrorSeverity.MEDIUM,
  client: null,
  auth: null,
  network: null,
  timeout: null,
  abort: null,
};

export const shouldLogErrorToSentry = (type: ClassifiedErrorType): boolean =>
  SENTRY_EVENT_POLICY[type] !== null;

const logClassifiedError = (
  error: Error,
  type: ClassifiedErrorType,
  data: Record<string, unknown>,
): void => {
  logBreadcrumb({
    level: 'error',
    message: `Classified ${type} error`,
    data,
  });

  const severity = SENTRY_EVENT_POLICY[type];
  if (severity !== null) {
    logErrorWithContext(error, {
      featureArea: FeatureArea.NETWORKING,
      responsibleTeam: ResponsibleTeam.FRONTEND_PLATFORM,
      severity,
      additionalContext: data,
    });
  }
};

/**
 * Safely parses JSON from a response clone. Returns undefined if not JSON.
 */
const extractResponseBody = async (
  response: Response,
): Promise<unknown | undefined> => {
  try {
    return await response.clone().json();
  } catch {
    return undefined;
  }
};

/**
 * Extracts a user-presentable message string from a parsed body.
 */
const extractServerMessage = (body: unknown): string | undefined => {
  if (typeof body === 'object' && body !== null) {
    const errorBody = body as Record<string, unknown>;
    if (typeof errorBody.error === 'string') {
      return errorBody.error;
    }
    if (typeof errorBody.message === 'string') {
      return errorBody.message;
    }
  }
  return undefined;
};

/**
 * REST layer — call for non-OK responses in authenticatedFetch /
 * unauthenticatedFetch. Classifies the HTTP error, applies the logging
 * policy, and throws the corresponding typed error.
 */
export const handleFetchError = async (response: Response): Promise<never> => {
  const statusDescription = `${response.status} ${response.statusText}`;
  const responseBody = await extractResponseBody(response);

  if (response.status >= 500) {
    const serverMessage = extractServerMessage(responseBody);
    const error = new ServerError(
      `Server error: ${statusDescription}`,
      response.status,
      serverMessage,
      responseBody,
    );
    logClassifiedError(error, 'server', {
      status: response.status,
      statusText: response.statusText,
      url: response.url,
    });
    throw error;
  }

  if ([401, 403].includes(response.status)) {
    const error = new AuthenticationError(
      `Authentication error: ${statusDescription}`,
      response.status,
      responseBody,
    );
    logClassifiedError(error, 'auth', { status: response.status });
    throw error;
  }

  const error = new ClientError(
    `Client error: ${statusDescription}`,
    response.status,
    responseBody,
  );
  logClassifiedError(error, 'client', {
    status: response.status,
    url: response.url,
  });
  throw error;
};

/**
 * REST layer — call in the catch block of fetch wrappers. Classifies
 * connectivity-level failures (DNS, connection refused, timeout, abort)
 * and throws the corresponding typed error. Never logs Sentry events —
 * these are transient client-side conditions.
 */
export const handleNetworkError = (error: unknown): never => {
  if (error instanceof BaseNetworkError) {
    throw error;
  }

  if (error instanceof Error) {
    if (isAbortLikeError(error)) {
      const configError = new RequestConfigError(
        `Client-side request error: ${error.message}`,
        error,
      );
      logClassifiedError(configError, 'timeout', { message: error.message });
      throw configError;
    }

    const networkError = new NetworkError(
      `Network communication failed: ${error.message}`,
      error,
    );
    logClassifiedError(networkError, 'network', { message: error.message });
    throw networkError;
  }

  const networkError = new NetworkError(
    `Network communication failed: ${JSON.stringify(error)}`,
  );
  logClassifiedError(networkError, 'network', {});
  throw networkError;
};

/**
 * GraphQL layer — call from UrqlClient's mapExchange.onError. Classifies
 * the CombinedError and applies the logging policy. Does not throw:
 * urql surfaces the original CombinedError to callers via result.error,
 * which the display layer classifies with the exact same logic.
 */
export const handleGraphQLError = (error: CombinedError): void => {
  // Persisted query misses are part of the normal protocol negotiation
  if (error.message.includes('PersistedQueryNotFound')) {
    return;
  }

  const classification = classifyError(error);
  logClassifiedError(error, classification.type, {
    type: classification.type,
    message: error.message,
    responseStatus: classification.statusCode,
    graphQLErrors: error.graphQLErrors,
  });
};

/**
 * Universal handler for imperative catch blocks that receive errors from
 * unknown sources. Classifies, applies the logging policy, and throws the
 * corresponding typed error.
 */
export const handleError = (error: unknown): never => {
  if (error instanceof BaseNetworkError) {
    throw error;
  }

  if (error instanceof CombinedError) {
    const classification = classifyError(error);
    const typedError = new GraphQLOperationError(
      error.message,
      error.graphQLErrors,
      error.networkError ?? undefined,
    );
    logClassifiedError(typedError, classification.type, {
      type: classification.type,
      message: error.message,
    });
    throw typedError;
  }

  return handleNetworkError(error);
};
